import React, { useState, lazy, Suspense } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { Chart } from "src/components/charts";

const MarkdownRenderer = lazy(() =>
    Promise.all([import("react-markdown"), import("remark-gfm")]).then(([mod, gfm]) => ({
        default: ({ children }) =>
            React.createElement(mod.default, { remarkPlugins: [gfm.default] }, children),
    }))
);

// ─── Calendar Heatmap ────────────────────────────────────────────────────────

const HEATMAP_COLORS = ["#ebedf0", "#9be9a8", "#40c463", "#30a14e", "#216e39"];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CELL = 12;
const GAP = 2;
const STEP = CELL + GAP;

function toDateStr(v) {
    return String(v).split("T")[0].split(" ")[0];
}

const CalendarHeatmap = ({ chart }) => {
    const { data, dateKey = "date", valueKey = "value" } = chart;
    const [tooltip, setTooltip] = useState(null); // { x, y, label }

    if (!data || data.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No data to display
            </Typography>
        );
    }

    // Auto-detect numeric column if specified valueKey doesn't exist in data
    const actualValueKey =
        data[0] && valueKey in data[0]
            ? valueKey
            : Object.keys(data[0]).find((k) => k !== dateKey && typeof data[0][k] === "number") ||
              valueKey;

    // Build lookup and compute colour thresholds
    const lookup = {};
    const positiveVals = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const row of data) {
        const d = toDateStr(row[dateKey]);
        const v = Number(row[actualValueKey]) || 0;
        lookup[d] = v;
        if (v > 0) positiveVals.push(v);
    }
    positiveVals.sort((a, b) => a - b);
    const thresholds = [
        0,
        positiveVals[Math.floor(positiveVals.length * 0.25)] || 1,
        positiveVals[Math.floor(positiveVals.length * 0.5)] || 2,
        positiveVals[Math.floor(positiveVals.length * 0.75)] || 4,
    ];
    const getColor = (v) => {
        if (!v) return HEATMAP_COLORS[0];
        for (let i = thresholds.length - 1; i >= 0; i -= 1) {
            if (v >= thresholds[i])
                return HEATMAP_COLORS[i + 1] || HEATMAP_COLORS[HEATMAP_COLORS.length - 1];
        }
        return HEATMAP_COLORS[1];
    };

    // Determine grid bounds (Sunday-aligned weeks)
    const sortedDates = Object.keys(lookup).sort();
    const rangeStart = new Date(`${sortedDates[0]}T00:00:00`);
    const rangeEnd = new Date(`${sortedDates[sortedDates.length - 1]}T00:00:00`);
    rangeStart.setDate(rangeStart.getDate() - rangeStart.getDay()); // back to Sunday
    rangeEnd.setDate(rangeEnd.getDate() + (6 - rangeEnd.getDay())); // forward to Saturday

    // Build weeks array
    const weeks = [];
    const cur = new Date(rangeStart);
    while (cur <= rangeEnd) {
        const week = [];
        for (let d = 0; d < 7; d += 1) {
            week.push(cur.toISOString().split("T")[0]);
            cur.setDate(cur.getDate() + 1);
        }
        weeks.push(week);
    }

    // Month label positions
    const monthLabels = [];
    let lastMonth = -1;
    weeks.forEach((week, wi) => {
        const m = new Date(week[0]).getMonth();
        if (m !== lastMonth) {
            const label = new Date(week[0]).toLocaleString("default", {
                month: "short",
                year: "2-digit",
            });
            monthLabels.push({ wi, label });
            lastMonth = m;
        }
    });

    const LEFT_OFFSET = 28; // space for day labels
    const TOP_OFFSET = 18; // space for month labels
    const svgW = LEFT_OFFSET + weeks.length * STEP;
    const svgH = TOP_OFFSET + 7 * STEP + 4;

    return (
        <Box sx={{ overflowX: "auto", mt: 1, position: "relative" }}>
            <svg width={svgW} height={svgH}>
                {/* Month labels */}
                {monthLabels.map(({ wi, label }) => (
                    <text key={wi} x={LEFT_OFFSET + wi * STEP} y={12} fontSize={9} fill="#6e7781">
                        {label}
                    </text>
                ))}
                {/* Day-of-week labels (Mon, Wed, Fri only to avoid clutter) */}
                {[1, 3, 5].map((d) => (
                    <text
                        key={d}
                        x={0}
                        y={TOP_OFFSET + d * STEP + CELL - 1}
                        fontSize={9}
                        fill="#6e7781"
                    >
                        {DAY_LABELS[d]}
                    </text>
                ))}
                {/* Cells */}
                {weeks.map((week, wi) =>
                    week.map((dateStr, di) => {
                        const v = lookup[dateStr] ?? 0;
                        return (
                            <rect
                                key={dateStr}
                                x={LEFT_OFFSET + wi * STEP}
                                y={TOP_OFFSET + di * STEP}
                                width={CELL}
                                height={CELL}
                                rx={2}
                                fill={getColor(v)}
                                onMouseEnter={() => {
                                    setTooltip({
                                        x: LEFT_OFFSET + wi * STEP,
                                        y: TOP_OFFSET + di * STEP - 22,
                                        label: v > 0 ? `${dateStr}: ${v}` : dateStr,
                                    });
                                }}
                                onMouseLeave={() => setTooltip(null)}
                                style={{ cursor: "default" }}
                            />
                        );
                    })
                )}
                {/* Inline SVG tooltip */}
                {tooltip && (
                    <g>
                        <rect
                            x={tooltip.x - 20}
                            y={tooltip.y}
                            width={90}
                            height={16}
                            rx={3}
                            fill="rgba(0,0,0,0.75)"
                        />
                        <text x={tooltip.x - 15} y={tooltip.y + 11} fontSize={10} fill="white">
                            {tooltip.label}
                        </text>
                    </g>
                )}
            </svg>
            {/* Legend */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                    ml: `${LEFT_OFFSET}px`,
                }}
            >
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    Less
                </Typography>
                {HEATMAP_COLORS.map((c) => (
                    <Box
                        key={c}
                        sx={{ width: CELL, height: CELL, borderRadius: "2px", bgcolor: c }}
                    />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    More
                </Typography>
            </Box>
        </Box>
    );
};

// Pivot long-format data into wide format for multi-series charts.
// Long format: each row has {xAxis, dimensionCol, valueCol}
// Wide format: each row has {xAxis, seriesKey1: val, seriesKey2: val, ...}
function pivotIfNeeded(data, xAxis, series) {
    if (!data || data.length === 0) return { chartData: data, chartSeries: series || [] };
    const firstRow = data[0];
    const dataKeys = Object.keys(firstRow);

    // If all series keys already exist as columns, no pivot needed
    if (series && series.length > 0 && dataKeys.includes(series[0].key)) {
        return { chartData: data, chartSeries: series };
    }

    // Find the dimension column (string, not xAxis) and value column (number, not xAxis)
    const dimCol = dataKeys.find((k) => k !== xAxis && typeof firstRow[k] === "string");
    const valCol = dataKeys.find((k) => k !== xAxis && typeof firstRow[k] === "number");
    if (!dimCol || !valCol) return { chartData: data, chartSeries: series || [] };

    // Pivot rows
    const pivoted = {};
    const dimKeys = new Set();
    // eslint-disable-next-line no-restricted-syntax
    for (const row of data) {
        const xVal = row[xAxis];
        if (!pivoted[xVal]) pivoted[xVal] = { [xAxis]: xVal };
        const rawDim = String(row[dimCol]);
        // Match to a provided series key (exact, then suffix match like "adadot / Backend Server" → "Backend Server")
        const matched = (series || []).find(
            (s) => rawDim === s.key || rawDim.endsWith(s.key) || rawDim.endsWith(`/ ${s.key}`)
        );
        const key = matched ? matched.key : rawDim;
        pivoted[xVal][key] = (pivoted[xVal][key] || 0) + row[valCol];
        dimKeys.add(key);
    }

    const chartData = Object.values(pivoted).sort((a, b) =>
        String(a[xAxis]).localeCompare(String(b[xAxis]))
    );
    const chartSeries =
        series && series.length > 0
            ? series.filter((s) => dimKeys.has(s.key))
            : Array.from(dimKeys).map((k) => ({ key: k, label: k }));

    return { chartData, chartSeries };
}

const ReportChart = ({ chart }) => {
    const { chartType, data, series } = chart;

    if (!data || data.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No data to display
            </Typography>
        );
    }

    if (chartType === "heatmap") {
        return <CalendarHeatmap chart={chart} />;
    }

    // Auto-detect xAxis: use specified key if it exists in data, else first string column
    const dataKeys = Object.keys(data[0]);
    const specifiedXAxis = chart.xAxis;
    const xAxis =
        specifiedXAxis && dataKeys.includes(specifiedXAxis)
            ? specifiedXAxis
            : dataKeys.find((k) => typeof data[0][k] === "string") || dataKeys[0];

    const { chartData, chartSeries: pivotedSeries } = pivotIfNeeded(data, xAxis, series);

    // Auto-add numeric columns not already in chartSeries
    const coveredKeys = new Set(pivotedSeries.map((s) => s.key));
    const extraSeries =
        chartData.length > 0
            ? Object.keys(chartData[0])
                  .filter(
                      (k) =>
                          k !== xAxis && !coveredKeys.has(k) && typeof chartData[0][k] === "number"
                  )
                  .map((k) => ({ key: k, label: k }))
            : [];
    const chartSeries = [...pivotedSeries, ...extraSeries];

    const spec = {
        chartType: chartType || "line",
        xAxis: { key: xAxis },
        series: chartSeries.map((s) => ({ key: s.key, label: s.label })),
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Chart spec={spec} data={chartData} height={300} />
        </Box>
    );
};

const ReportTable = ({ table }) => {
    // Claude sometimes uses "data" instead of "rows"
    const rows = table.rows || table.data || [];
    const rawColumns = table.columns || (rows.length > 0 ? Object.keys(rows[0]) : []);
    // Claude sometimes returns columns as {key, label} objects — normalise to {key, label} pairs
    const columns = rawColumns.map((col) =>
        typeof col === "object" && col !== null
            ? { key: col.key, label: col.label || col.key }
            : { key: col, label: col }
    );
    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 360 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map(({ key, label }) => (
                            <TableCell key={key} sx={{ fontWeight: 600 }}>
                                {label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={columns.map((c) => String(row[c.key] ?? "")).join("|")}>
                            {columns.map(({ key }) => (
                                <TableCell key={key}>{String(row[key] ?? "")}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ReportOutput = ({ output }) => {
    if (!output) return null;

    return (
        <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {output.title}
            </Typography>
            {output.explanation && (
                <Box
                    sx={{
                        mb: 1,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                        "& p": { margin: "0 0 6px 0" },
                        "& p:last-child": { margin: 0 },
                        "& ul, & ol": { paddingLeft: "1.5em", margin: "0 0 6px 0" },
                        "& strong": { fontWeight: 600 },
                    }}
                >
                    <Suspense fallback={output.explanation}>
                        <MarkdownRenderer>{output.explanation}</MarkdownRenderer>
                    </Suspense>
                </Box>
            )}
            {output.type === "chart" && output.chart && <ReportChart chart={output.chart} />}
            {output.type === "table" && output.table && <ReportTable table={output.table} />}
            {output.type === "text" && (
                <Box
                    sx={{
                        fontSize: "0.875rem",
                        "& p": { margin: "0 0 8px 0" },
                        "& p:last-child": { margin: 0 },
                        "& ul, & ol": { paddingLeft: "1.5em", margin: "0 0 8px 0" },
                        "& strong": { fontWeight: 600 },
                    }}
                >
                    <Suspense fallback={output.text}>
                        <MarkdownRenderer>{output.text}</MarkdownRenderer>
                    </Suspense>
                </Box>
            )}
        </Box>
    );
};

export default ReportOutput;
